import React, { FC, memo, useState, useEffect, useMemo } from 'react'

// eslint-disable-next-line import/no-unresolved
import { type Tizen } from 'tizen-common-web'

import { isTizen } from '@/utils/utils'

import { PerformanceMonitorElem, StatsBarElem, StatsTextElem } from './styles'

const tizen = (window as { [key: string]: any })['tizen'] as Tizen

const PerformanceMonitorRaw: FC = () => {
  const [availableMemory, setAvailableMemory] = useState<number[]>([])
  const [cpuLoad, setCpuLoad] = useState<number[]>([])
  const historyLimit = 100 * 2
  const refreshTime = 2000
  let memTimeout: ReturnType<typeof setTimeout>
  let cpuTimeout: ReturnType<typeof setTimeout>

  useEffect(() => {
    getMemStats()
    getCpuStats()
    return () => {
      clearTimeout(memTimeout)
      clearTimeout(cpuTimeout)
    }
  }, [])

  const getMemStats = () => {
    const value =
      Math.round(tizen?.systeminfo?.getAvailableMemory() / 1024 / 1024) ||
      getRandomValue('mem')

    setAvailableMemory((availableMemory) => {
      if (availableMemory.length >= historyLimit) {
        availableMemory.pop()
      }
      return [value, ...availableMemory]
    })

    memTimeout = setTimeout(() => {
      getMemStats()
    }, refreshTime)
  }

  const getCpuStats = () => {
    const onSuccess = (cpu: any) => {
      setCpuLoadHandler(cpu.load)
    }

    if (isTizen() && tizen?.systeminfo) {
      tizen.systeminfo.getPropertyValue('CPU', onSuccess)
    } else {
      setCpuLoadHandler(getRandomValue('cpu'))
    }

    cpuTimeout = setTimeout(() => {
      getCpuStats()
    }, refreshTime)
  }

  const setCpuLoadHandler = (value: any) => {
    setCpuLoad((cpuLoad) => {
      if (cpuLoad.length >= historyLimit) {
        cpuLoad.pop()
      }
      return [Math.round(value * 100), ...cpuLoad]
    })
  }

  const getRandomValue = (type: string) => {
    let value = 0
    if (type === 'mem') {
      value = Math.round(Math.random() * 1000)
    } else if (type === 'cpu') {
      value = Math.random()
    }
    return value
  }

  const getPercentage = (value: number, total: number) =>
    Math.round((value / total) * 100)

  const getBarColor = (value: number) => {
    if (value >= 80) {
      return 'red'
    } else if (value >= 70 && value < 85) {
      return 'orange'
    }
    return 'green'
  }

  const totalMemory = useMemo(
    () => Math.round(tizen?.systeminfo?.getTotalMemory() / 1024 / 1024) || 1000,
    [],
  )

  return (
    <PerformanceMonitorElem>
      {!tizen?.systeminfo && 'Performance monitor works on Tizen only'}
      <StatsBarElem>
        {availableMemory.map((value: number, i: number) => {
          const percentage = getPercentage(value, totalMemory)
          return (
            <i
              key={'mem' + i}
              style={{
                height: `${percentage}%`,
                backgroundColor: getBarColor(percentage),
              }}
            ></i>
          )
        })}
      </StatsBarElem>
      <StatsTextElem>
        RAM available {availableMemory[0]} / {totalMemory} Mb{' '}
        {!tizen?.systeminfo && '(random)'}
      </StatsTextElem>

      <StatsBarElem>
        {cpuLoad.map((value: number, i: number) => {
          return (
            <i
              key={'cpu' + i}
              style={{
                height: `${value}%`,
                backgroundColor: getBarColor(value),
              }}
            ></i>
          )
        })}
      </StatsBarElem>
      <StatsTextElem>
        CPU load {cpuLoad[0]} % {!tizen?.systeminfo && '(random)'}
      </StatsTextElem>
    </PerformanceMonitorElem>
  )
}

export const PerformanceMonitor = memo(PerformanceMonitorRaw)
